import { ReactNode, Suspense } from 'react';

import {
  Button,
  EmptyState,
  EmptyStateText,
  EmptyStateTitle,
  IconExclamationCircle,
  ProgressDots,
} from '@spotify-internal/encore-web';
import { isNotFoundError } from '@spotify/confidence-graphql';

import { ErrorBoundary } from '../ErrorBoundary';

export type SectionResourceGuardProps = {
  resourceLabel: string;
  children: ReactNode;
};

export const SectionResourceGuard = ({
  resourceLabel,
  children,
}: SectionResourceGuardProps): ReactNode => {
  const normalizedResourceLabel = resourceLabel.toLowerCase();

  return (
    <ErrorBoundary
      renderFallback={({ error }) => {
        const resourceMissing = isNotFoundError(error);

        return (
          <EmptyState
            variant="contextual"
            icon={IconExclamationCircle}
            iconColorSet="negative"
            iconTitle={resourceMissing ? 'Not found' : 'Error'}
            role="alert"
          >
            <EmptyStateTitle>
              {resourceMissing
                ? `${resourceLabel} not found`
                : `Could not load ${normalizedResourceLabel}`}
            </EmptyStateTitle>
            <EmptyStateText>
              {resourceMissing
                ? `The requested ${normalizedResourceLabel} could not be found.`
                : error?.message ||
                  `Could not load ${normalizedResourceLabel}.`}
            </EmptyStateText>
            {!resourceMissing && (
              <Button
                variant="secondary"
                size="small"
                onClick={() => window.location.reload()}
              >
                Reload page
              </Button>
            )}
          </EmptyState>
        );
      }}
    >
      <Suspense
        fallback={
          <ProgressDots ariaValueText={`Loading ${normalizedResourceLabel}`} />
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

SectionResourceGuard.displayName = 'Section.ResourceGuard';
